import axios from "axios";
import {useEffect} from "react";
import {useRouter} from "next/router";
import {
  REQUEST_DEVICE_HEADER,
  REQUEST_ORIGIN_URL_HEADER,
  REQUEST_SOURCE_HEADER,
  REQUEST_USER_AGENT,
  SOURCE_FRONTEND_BACKEND,
  SOURCE_FRONTEND_CLIENT
} from "./constants";
import {getApiBaseUrl} from "./getApiBaseUrl";
import Cookies from "js-cookie";
import {parse} from 'cookie';
import {useAtom} from "jotai";
import {nextSessionAtom} from "../store/sessionAtom";
import {getSession} from "next-auth/react";

const logAllRequests = false // process.env.NODE_ENV == "production";
const logResponses = false // process.env.NODE_ENV == "production";

const client = axios.create({
  proxy: false,
  timeout: 30000,
  validateStatus: function (status) {
    return status < 600;
  }
});

// Log server side the intercepted response if it's error
// pm2 adds all logs to rotated log files - configured in the server
client.interceptors.response.use(function (response) {
  // Any status code that lie within the range of 2xx cause this function to trigger
  // Do something with response data
  if (logResponses && typeof window == 'undefined') {
    console.log(response)
  }

  return response;
}, function (error) {
  // Any status codes that falls outside the range of 2xx cause this function to trigger
  console.error("Response error: ", error)
  return Promise.reject(error.response);
})


client.interceptors.request.use(function (config) {
  if (logAllRequests && typeof window == 'undefined') {
    // Log the request information
    console.log('Request:', config);
  }
  return config;
}, function (error) {
  // Do something with request error
  console.error('Request Error: ', error);
  return Promise.reject(error);
});

async function fetchUserIP() {
  try {
    const response = await fetch('/api/getUserIp');
    const data = await response.json();
    return data.userIp; // The IP address from the server
  } catch (error) {
    console.error('Error fetching user IP:', error);
  }
}


const AxiosInterceptor = ({children}: any) => {
  const router = useRouter();
  const [sessionAtom] = useAtom(nextSessionAtom)
  const {session, status} = sessionAtom;

  useEffect(() => {
    // HTTP request interceptor
    const reqInterceptor = client.interceptors.request.use(function (config) {
      // Do something before request is sent
      if (config && config.headers) {
        // Handle client-side authentication
        if (status === "authenticated" && session?.user?.accessToken) {
          config.headers["Authorization"] = `Bearer ${session.user.accessToken}`;
        }

        // Add page information
        if (router.pathname) {
          config.headers["X-Invoca-Frontend-Page"] = router.pathname;
        }
      }

      return config;
    }, function (error) {
      // Do something with request error
      console.error(error)
      return Promise.reject(error);
    });

    return () => {
      // cleanup
      client.interceptors.request.eject(reqInterceptor);
    }
  }, [session, status, router.pathname])

  return children;
}


export const request = async ({...options}: any) => {
  // Create headers object if it doesn't exist
  if (!options.headers) {
    options.headers = {};
  }

  // ----------------------------------
  // frontend-backend requests handling
  // ----------------------------------
  if (typeof window === 'undefined') {
    const protocol = options.context?.req?.headers['x-forwarded-proto'] || 'https';
    const host = options.host || 'musix.com';
    
    // Use the frontendUrl from context if available, otherwise build it
    const frontendUrl = options.context?.frontendUrl || 
                       options.frontendUrl || 
                       `${protocol}://${host}${options.context?.resolvedUrl || '/'}`;

    options.headers[REQUEST_ORIGIN_URL_HEADER] = frontendUrl;
    options.headers[REQUEST_SOURCE_HEADER] = SOURCE_FRONTEND_BACKEND;

    if (options.context?.did && !(options.cookies && options.cookies[REQUEST_DEVICE_HEADER])) {
      options.headers[REQUEST_DEVICE_HEADER] = options.context?.did;
    }

    if (options.cookies && options.cookies[REQUEST_DEVICE_HEADER]) {
      const did = parse(options.cookies[REQUEST_DEVICE_HEADER]);
      options.headers[REQUEST_DEVICE_HEADER] = did[REQUEST_DEVICE_HEADER] || options.cookies[REQUEST_DEVICE_HEADER];
    }

    // Add request-specific headers from context
    if (options.context?.requestHeaders) {
      Object.entries(options.context.requestHeaders).forEach(([key, value]) => {
        options.headers[key] = value;
      });
    }

    // If SSR request has the context object attach the auth header
    if (options.context) {
      const session: any = await getSession(options.context)
      if (session?.accessToken) {
        options.headers.Authorization = `Bearer ${session.accessToken}`;
      }
    }
  }
  // --------------------------
  // frontend requests handling
  // --------------------------
  else {
    options.headers[REQUEST_SOURCE_HEADER] = SOURCE_FRONTEND_CLIENT;
    options.headers[REQUEST_ORIGIN_URL_HEADER] = window.location.href;
    options.headers[REQUEST_USER_AGENT] = navigator.userAgent;
    
    const xDeviceId = Cookies.get(REQUEST_DEVICE_HEADER);
    if (xDeviceId) {
      options.headers[REQUEST_DEVICE_HEADER] = xDeviceId;
    }
  }

  // ---------------
  // Global handling
  // ---------------
  if (options.ip) {
    options.headers["X-Invoca-Frontend-IP"] = options.ip;
  }

  // Handle the base url from request host
  // Client side we use the origin
  // Server side we pass the host from context.request
  let baseUrl;
  if (typeof window !== 'undefined') {
    baseUrl = {
      baseURL: getApiBaseUrl(window.origin)
    }
  } else if (options.host) {
    baseUrl = {
      baseURL: getApiBaseUrl(options.host)
    }
  } else {
    // fallback
    baseUrl = {
      baseURL: getApiBaseUrl("musix.com")
    }
  }

  const extendedOptions = {...options, ...baseUrl};

  // If we have a session token, ensure it's set in the request headers
  if (options.session?.accessToken) {
    extendedOptions.headers.Authorization = `Bearer ${options.session.accessToken}`;
  }

  return client(extendedOptions);
}

export default client;
export {AxiosInterceptor};